import { FinancesServerResponseObject } from "../../services";
import { Values } from "../../components/Charts";
import { CARDS } from "./constants";
import { Transaction } from "../../services/transactions/types";
import { TransactionType } from "../../utils";
import { CardProps, TransactionTone } from "./types";

export const formatTransactionDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

export const getTransactionTitle = (tx: Transaction): string =>
  tx.note || tx.transactionType || "Transaction";

/**
 * Classify a transaction for display. Only income adds money and only expenses
 * subtract it; transfers, loan payments and market updates move value between a
 * user's own holdings, so they read as neutral rather than a red loss.
 */
export const getTransactionTone = (tx: Transaction): TransactionTone => {
  if (tx.transactionType === TransactionType.INCOME) {
    return "positive";
  }
  if (tx.transactionType === TransactionType.EXPENSE) {
    return "negative";
  }
  return "neutral";
};

export const getPercentage = (val: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((val / total) * 100);
};

/**
 * Calculate Savings Rate: (Income - Expenses) / Income * 100
 * Returns percentage between 0 and 100, or 0 if income is 0
 * Negative savings rates are clamped to 0 (spending more than earning)
 */
export const calculateSavingsRate = (
  income: number,
  expenses: number,
): number => {
  if (income === 0) return 0;
  const rate = ((income - expenses) / income) * 100;
  return Math.max(0, Math.min(100, rate));
};

/**
 * Calculate Investment Allocation: Investments / Total Assets * 100
 * Returns percentage between 0 and 100, or 0 if total assets is 0
 */
export const calculateInvestmentAllocation = (
  investments: number,
  accounts: number,
  savings: number,
): number => {
  const totalAssets = accounts + investments + savings;
  if (totalAssets === 0) return 0;
  const allocation = (investments / totalAssets) * 100;
  return Math.max(0, Math.min(100, allocation));
};

export const calculateInvestmentRunway = (
  investments: number,
  monthlyBurnRate: number,
): number => {
  if (monthlyBurnRate <= 0) return 0;
  return investments / monthlyBurnRate;
};

/**
 * Calculate Debt-to-Asset Ratio: Liabilities / Total Assets
 * Returns ratio or 0 if total assets is 0
 */
export const calculateDebtToAssetRatio = (
  liabilities: number,
  accounts: number,
  investments: number,
  savings: number,
): number => {
  const totalAssets = accounts + investments + savings;
  if (totalAssets === 0) return 0;
  return Math.abs(liabilities) / totalAssets;
};

export const getCardAndChartData = (result: FinancesServerResponseObject) => {
  const tempCards: CardProps[] = [...CARDS];
  tempCards[0].value = result.balance;
  tempCards[1].value = result.income;
  tempCards[2].value = result.expenses;
  tempCards[3].value = result.accounts;
  tempCards[4].value = result.savings;
  tempCards[5].value = result.investments;
  tempCards[6].value = result.liabilities;
  tempCards[7].value = result.netWorth;
  if (tempCards[7].value !== null && tempCards[7].value < 0) {
    tempCards[7].color = "var(--color-error)";
  }

  // Two different questions, deliberately kept apart. `hasData` is "does this user have money at
  // all" — it drives the first-run onboarding state, so an incomplete valuation must NOT clear it
  // (someone whose quote is still in flight is not a new user). `hasChartData` is "is there
  // anything to draw", which an incomplete valuation does clear: the breakdown slices are shares of
  // a total that cannot be computed, so the chart would be an empty frame rather than an empty
  // state. Gate a chart on `hasChartData`, never on `hasData`.
  const hasData =
    result.accounts !== 0 ||
    (result.investments !== null && result.investments !== 0) ||
    result.liabilities !== 0 ||
    result.savings !== 0;
  if (result.investments === null || result.netWorth === null) {
    return {
      tempCards,
      chartData: [],
      chartData2: [],
      hasData,
      hasChartData: false,
    };
  }

  const total = result.accounts + result.investments + result.savings;
  const liabilities = result.liabilities * -1;
  const totalAmount =
    result.accounts + result.investments + result.savings + liabilities;

  let chartData: Values[] = [];
  let chartData2: Values[] = [];
  const hasChartData =
    result.accounts !== 0 ||
    result.investments !== 0 ||
    liabilities !== 0 ||
    result.savings !== 0;
  if (hasChartData) {
    chartData = [
      {
        id: 0,
        value: getPercentage(result.accounts, total),
        label: "Bank Account",
        color: "var(--color-info)",
      },
      {
        id: 1,
        value: getPercentage(result.investments, total),
        label: "Investments",
        color: "var(--color-blue)",
      },
      {
        id: 2,
        value: getPercentage(result.savings, total),
        label: "Savings",
        color: "var(--color-purple)",
      },
    ];
    chartData2 = [
      {
        id: 0,
        value: getPercentage(total, totalAmount),
        label: "Assets",
        color: "var(--color-success)",
      },
      {
        id: 1,
        value: getPercentage(liabilities, totalAmount),
        label: "Liabilities",
        color: "var(--color-error)",
      },
    ];
  }
  return { tempCards, chartData, chartData2, hasData, hasChartData };
};
